import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import ReduxDataTable from '../redux_data_table/ReduxDataTable';
import { assetsRestCrudThunksForSupplier } from '../../thunks/assets_thunks';
import { SUB_ASSETS_TARGET_COLLECTION } from '../../reducers/assets_reducer';
import { getEntityById, nullSafeGet } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import AssetWithQRCodeDisplay from '../asset_row_display/AssetWithQRCodeDisplay';
import { EmptyState } from '../empty_state/EmptyState';
import { getBuyerCompanySettingValue, PERMISSION_NAMES } from '../../utils/AuthUtils';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import OWButtonWithSupplierManagedLock from '../common/OWButtonWithSupplierManagedLock';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import LinkSubAssetModal from '../link_sub_asset/LinkSubAssetModal';
import UnlinkSubAssetButton from '../link_sub_asset/UnlinkSubAssetButton';

const SUB_ASSETS_PAGE_SIZE = 50;

const SupplierAssetsDetailSubAssetsPage: FC<any> = ({
	history,
	location,
	match,
	asset,
	assets,
	currentUser,
}) => {
	const assetId = match.params.id;
	const [linkModalVisible, setLinkModalVisible] = useState(false);
	// ReduxDataTable fetches on mount; keying it by `${assetId}-${refreshToken}` makes it
	// refetch both when navigating between assets and when we bump the token after a
	// link/unlink (its only external refresh hook).
	const [refreshToken, setRefreshToken] = useState(0);
	const refreshTable = useCallback(() => setRefreshToken((token) => token + 1), []);

	const allowSubAssets = getBuyerCompanySettingValue(ROLE_TYPES.SUPPLIER)(
		'assetConfig.allowSubAssets',
		{},
		currentUser,
		false
	);

	const onRow = useCallback(
		(record) => ({
			onClick: () => {
				const path = `/supplier/assets/detail/${nullSafeGet('id', record)}/details`;
				history.push(
					getLinkWIthBackLinkParams(location, 'Back to Asset', path, { preserve: true })
				);
			},
		}),
		[history, location]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'name',
				render: (text, record) => (
					<AssetWithQRCodeDisplay asset={record} hideRightColumn hideSubAssetOf />
				),
			},
			{
				title: '',
				key: 'unlink',
				width: 140,
				align: 'right' as const,
				render: (text, record) => (
					<UnlinkSubAssetButton
						parentAssetId={assetId}
						childAssetId={nullSafeGet('id', record)}
						onUnlinked={refreshTable}
					/>
				),
			},
		],
		[assetId, refreshTable]
	);

	const rightActions = allowSubAssets ? (
		<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_ASSETS}>
			<div className="flex flex-row items-center justify-end gap-3">
				<OWButtonWithSupplierManagedLock
					size="large"
					// Wait for the asset to hydrate so the picker opens with a real parent id
					// and location scope (getEntityById returns {} until then).
					disabled={!nullSafeGet('id', asset)}
					onClick={() => setLinkModalVisible(true)}
				>
					Link Sub Asset
				</OWButtonWithSupplierManagedLock>
				<OWButtonWithSupplierManagedLock
					size="large"
					type="primary"
					onClick={() =>
						history.push(
							// Carry this asset's back-link context into the new-asset form so the
							// created sub asset's back button can walk all the way back.
							getLinkWIthBackLinkParams(
								location,
								'Back to Asset',
								`/supplier/assets/overview/new?parentId=${assetId}`,
								{ preserve: true }
							)
						)
					}
				>
					Create sub asset
				</OWButtonWithSupplierManagedLock>
			</div>
		</AccessPermissionChecker>
	) : null;

	return (
		<div className="flex flex-col">
			<ReduxDataTable
				key={`${assetId}-${refreshToken}`}
				entityCollectionName="assets"
				collectionName="assets"
				targetCollectionName={SUB_ASSETS_TARGET_COLLECTION}
				stateSlice={assets}
				fetchData={assetsRestCrudThunksForSupplier}
				tableColumns={columns}
				onTableRow={onRow}
				showHeader={false}
				hideFilters
				filterConfig={[]}
				preAppliedFilters={{ parentIds: assetId }}
				initialPagination={{ current: 1, pageSize: SUB_ASSETS_PAGE_SIZE }}
				initialSorting={{ sort_by: 'name', order: 'ascend' }}
				rightActions={rightActions}
				emptyState={
					<EmptyState
						graphic={
							<img
								alt="No sub assets yet"
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
							/>
						}
						headline={'No sub assets yet.'}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440 }}>
									This asset doesn't have any sub assets associated with it.
								</div>
							</div>
						}
					/>
				}
			/>
			{linkModalVisible ? (
				<LinkSubAssetModal
					// getEntityById returns {} (truthy) before the asset hydrates, so gate on
					// a real id rather than `asset || …` — otherwise parentAssetId is undefined
					// and we'd POST to /…/undefined/link_sub_assets.
					parentAsset={nullSafeGet('id', asset) ? asset : { id: assetId }}
					onCancel={() => setLinkModalVisible(false)}
					onSuccess={() => {
						setLinkModalVisible(false);
						refreshTable();
					}}
				/>
			) : null}
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	asset: getEntityById(ownProps.match.params.id, state.assets),
	assets: state.assets,
	currentUser: state.session.currentUser,
});

export default withRouter<any, any>(connect(mapStateToProps)(SupplierAssetsDetailSubAssetsPage));
